import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, useBreakpoint } from '@factorialco/deprecated-design-system'
import { F0Button, F0Card, F0Icon } from '@factorialco/f0-react'
import { AutoGrid, F1SearchBox, Spinner } from '@factorialco/f0-react/dist/experimental'
import { AlertCircleLine, CheckCircleLine, Filter, Pig } from '@factorialco/f0-react/icons/app'
import {
  BillingAddonReferenceFragment,
  GetUpsellDiscountsDocument,
  MarketplaceModulesDataCollectionDocument,
  MarketplaceModulesDataCollectionQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { locale } from 'modules/marketplace_modules/components/utils/locales'
import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import { useModuleStatus } from 'modules/marketplace_modules/hooks/useModuleStatus'
import { getInstalledStatus } from 'modules/marketplace_modules/lib/getInstalledStatus'

type FilterStatus = 'all' | 'installed' | 'not-installed'

type ModuleFromQuery = NonNullable<
  MarketplaceModulesDataCollectionQuery['modules']
>['datoModulesConnection']['nodes'][number]

type IncludedInPlanFromQuery = NonNullable<ModuleFromQuery['includedInPlan']>[number]

/*
 * Displays a filterable and searchable list of marketplace modules with their installation status.
 * Modules can be installed in two ways:
 * 1. Via subscription (direct addon purchase)
 * 2. Via bundle (included in a plan bundle)
 *
 * The component checks subscription status first, then falls back to bundle status if not found.
 */
const List = () => {
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')

  const { breakpoint } = useBreakpoint()
  const isLargeScreen = breakpoint === 'xxxl'
  const navigate = useNavigate()
  const { access, company } = useCurrentSession()
  const location = useLocation()
  const { search: searchParams } = location
  const { getDetailPath } = useMarketplaceModulesPath()

  const queryParams = useMemo(() => new URLSearchParams(searchParams), [searchParams])
  const upsellOrigin = queryParams.get('upsell_origin')
  const preview = queryParams.get('preview') === 'true'

  const datoFormatLocale = locale[access.locale]
  const countryCodeForCompany = countryCode(company.legalEntities[0]?.country || '')

  const { data: discountsData } = useQuery(GetUpsellDiscountsDocument)

  // Helper to extract addon references from modules
  const getModuleAddonReferences = (modules: ModuleFromQuery[]) => {
    return modules
      .filter((module) => module.billingAddonReference && module.billingAddonReference.length > 0)
      .map((module) => module.billingAddonReference || [])
      .filter((refs) => refs.length > 0)
  }

  // Store the addon references in state so the hook can use them
  const [moduleAddonReferences, setModuleAddonReferences] = useState<
    BillingAddonReferenceFragment[][]
  >([])

  // Call the hook with all addon references

  const { data: modulesData, loading: modulesLoading } = useQuery(
    MarketplaceModulesDataCollectionDocument,
    {
      variables: {
        countryCode: countryCodeForCompany,
        locale: datoFormatLocale,
        preview,
        search,
      },
    }
  )

  const getModuleIncludedInPlan = (modules: ModuleFromQuery[]) => {
    return modules
      .filter((module) => module.includedInPlan && module.includedInPlan.length > 0)
      .map((module) => ({
        moduleName: module.name || '',
        includedInPlan: module.includedInPlan || [],
      }))
  }

  const [moduleIncludedInPlan, setModuleIncludedInPlan] = useState<
    Array<{ moduleName: string; includedInPlan: IncludedInPlanFromQuery[] }>
  >([])

  const { getModuleStatus: getModuleStatusFromHook, loading: moduleStatusLoading } =
    useModuleStatus(moduleAddonReferences, {})

  useEffect(() => {
    if (modulesData?.modules.datoModulesConnection.nodes) {
      setModuleAddonReferences(
        getModuleAddonReferences(modulesData.modules.datoModulesConnection.nodes)
      )

      setModuleIncludedInPlan(
        getModuleIncludedInPlan(modulesData.modules.datoModulesConnection.nodes)
      )
    }
  }, [modulesData])

  const getModuleStatus = (module: ModuleFromQuery) => {
    return getModuleStatusFromHook({
      moduleName: module.name || null,
      billingAddonReference: module.billingAddonReference || null,
      moduleIncludedInPlan,
    })
  }

  const containerStyle = {
    ...(isLargeScreen && { width: '1920px', margin: '0 auto' }),
  }

  const hasDiscount = (item: ModuleFromQuery) => {
    const data = discountsData?.upsell?.upsellDiscountsConnection?.nodes
    const discount = data?.find(
      (discount_item) => discount_item.module === item.chargebeeModuleName
    )
    return discount
  }

  const installedLabel = (item: ModuleFromQuery) => {
    const status = getModuleStatus(item)

    return getInstalledStatus(
      status.isInstalled,
      status.variant,
      status.hasEnterpriseVariant ?? false,
      status.from
    )
  }

  return (
    <Box style={containerStyle} gap="s24" paddingTop="s24">
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Box flexDirection="row" gap="s8">
          <F0Icon icon={Filter} width="s32" height="s32" />
          <F0Button
            variant={filterStatus === 'installed' ? 'primary' : 'outline'}
            onClick={() => setFilterStatus(filterStatus === 'installed' ? 'all' : 'installed')}
            label={i18n.t('marketplace.revamp.in_your_plan')}
          />
          <F0Button
            variant={filterStatus === 'not-installed' ? 'primary' : 'outline'}
            onClick={() =>
              setFilterStatus(filterStatus === 'not-installed' ? 'all' : 'not-installed')
            }
            label={i18n.t('marketplace.revamp.not_in_your_plan')}
          />
        </Box>
        <Box alignItems="flexEnd">
          <F1SearchBox value={search} onChange={setSearch} placeholder="Search" clearable />
        </Box>
      </Box>
      {(modulesLoading || moduleStatusLoading) && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <Spinner />
        </Box>
      )}
      <AutoGrid gap="4">
        {modulesData?.modules.datoModulesConnection.nodes
          .filter((module) => {
            if (filterStatus === 'all') return true

            const isInstalled = getModuleStatus(module).isInstalled

            if (filterStatus === 'installed') {
              return isInstalled
            }

            if (filterStatus === 'not-installed') {
              return !isInstalled
            }

            return true
          })
          .sort((a, b) => {
            if (filterStatus === 'all') {
              const aInstalled = getModuleStatus(a).isInstalled
              const bInstalled = getModuleStatus(b).isInstalled

              if (!aInstalled && bInstalled) return -1

              if (aInstalled && !bInstalled) return 1
            }

            return 0
          })
          .map((module) => {
            const moduleStatus = getModuleStatus(module)
            const moduleDiscount = hasDiscount(module)

            return (
              <F0Card
                onClick={() => {
                  navigate(
                    getDetailPath(
                      module.slug,
                      `upsell_origin=${upsellOrigin ? upsellOrigin : 'modules_list'}`
                    ),
                    {
                      state: { from: location.pathname },
                    }
                  )
                }}
                key={module.id}
                image={module.thumbnailImage?.url || ''}
                title={module.translatedName || ''}
                description={module.shortDescription || ''}
                metadata={[
                  {
                    icon: moduleStatus.isInstalled ? CheckCircleLine : AlertCircleLine,
                    property: {
                      type: 'status' as const,
                      value: {
                        label: installedLabel(module).label,
                        status: moduleStatus.isInstalled
                          ? ('positive' as const)
                          : ('neutral' as const),
                      },
                    },
                  },
                  moduleDiscount && !moduleStatus.isInstalled
                    ? {
                        icon: Pig,
                        property: {
                          type: 'status' as const,
                          value: {
                            label: i18n.t('marketplace.revamp.list.discount', {
                              percentage: Math.floor(moduleDiscount?.percentage || 0),
                            }),
                            status: 'warning' as const,
                          },
                        },
                      }
                    : undefined,
                ].filter(Boolean)}
              />
            )
          })}
      </AutoGrid>
    </Box>
  )
}

export default List
